import { ChartDataPoint } from 'features/systemLens'
import {
  applyAxisBoundEdit,
  applyAxisBuffer,
  calculateOverallRangeWithBuffer,
  computeScaleModeTransitionRanges,
  CustomAxisRangeMap,
  getSharedAutoRange,
  hasAnyCustomBound,
  hasFiniteRange,
  pruneCustomAxisRanges,
  SensorRangeMap,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { parseCommittedNumber } from 'features/timeSeries/utils/numericInput'
import { getAllActiveItems } from 'features/timeSeries/utils/timeSeriesUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BaseSensorEntry,
  SensorAxisAssignment,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

interface UseTimeSeriesUXProps {
  selectedSensors: BaseSensorEntry[]
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  unitMapping: Record<string, string>
  chartData: ChartDataPoint[]
}

export const useTimeSeriesUX = ({
  selectedSensors,
  derivedValues,
  unitMapping,
  chartData,
}: UseTimeSeriesUXProps) => {
  const [expandedAxes, setExpandedAxes] = useState<Record<string, boolean>>({})
  const [xAxisSensorId, setXAxisSensorId] = useState<string | null>(null)

  const [singleScaleMode, setSingleScaleMode] = useState(false)
  const [autoScaleMode, setAutoScaleMode] = useState(true)
  const [customAxisRanges, setCustomAxisRanges] = useState<CustomAxisRangeMap>(
    {}
  )
  const [customXAxisRange, setCustomXAxisRange] = useState<{
    min?: number
    max?: number
  }>({})
  const [sensorAxisAssignment, setSensorAxisAssignment] =
    useState<SensorAxisAssignment>({})
  const [highlightedSensorId, setHighlightedSensorId] = useState<string | null>(
    null
  )
  const [secondaryHighlightedSensorIds, setSecondaryHighlightedSensorIds] =
    useState<string[]>([])
  const [hideAllAlerts, setHideAllAlerts] = useState(false)

  /**
   * The last value the user committed in each column (min / max), regardless
   * of which pen it was typed on. Switching to single scale applies these to
   * every pen. Deliberately not persisted: a fresh mount falls back to the
   * current shared/computed range.
   */
  const lastModifiedBoundRef = useRef<{ min?: number; max?: number }>({})

  /**
   * While auto-scale is off, a pen's auto-derived range is FROZEN so the
   * fallback axes don't shift underneath the user's custom edits. The freeze
   * is per pen, and only once that pen has settled on finite data:
   * - a pen with no data yet keeps refreshing until real data arrives, so an
   *   empty snapshot is never frozen (the earlier whole-map cache froze the
   *   `{}` computed before hydration populated the pens, leaving every pen on
   *   the default [0,1] axis forever);
   * - a pen added while custom bounds exist still gets a fresh entry;
   * - a removed pen's frozen entry is dropped with it.
   */
  const sensorRangesCustomScaleCache = useRef<SensorRangeMap | null>(null)

  const sensorRanges = useMemo(() => {
    const sensorData: SensorRangeMap = {}

    const allItems = getAllActiveItems(selectedSensors, derivedValues)

    allItems.forEach((item) => {
      if (item) {
        const itemName = (item.name as string) || item.id
        const unit = item.id.startsWith('derived_')
          ? ''
          : unitMapping[itemName] || 'No Unit'
        // min/max stay undefined until a finite data point is seen — never an
        // Infinity sentinel, so "no data" cannot leak into the UI as ±Infinity.
        sensorData[item.id] = { unit }
      }
    })

    if (chartData.length > 0) {
      allItems.forEach((item) => {
        if (item && sensorData[item.id]) {
          chartData.forEach((point) => {
            const value = point[item.id]
            if (typeof value === 'number' && !isNaN(value)) {
              const current = sensorData[item.id]
              current.min =
                current.min === undefined ? value : Math.min(current.min, value)
              current.max =
                current.max === undefined ? value : Math.max(current.max, value)
            }
          })
        }
      })
    }

    if (singleScaleMode && allItems.length > 0) {
      const overallRange = calculateOverallRangeWithBuffer(allItems, sensorData)

      if (overallRange) {
        const { bufferedMin, bufferedMax } = overallRange
        allItems.forEach((item) => {
          if (sensorData[item.id]) {
            sensorData[item.id].min = bufferedMin
            sensorData[item.id].max = bufferedMax
          }
        })
      }
    } else {
      // Multiple scale mode - apply buffer to each sensor individually
      allItems.forEach((item) => {
        const data = sensorData[item.id]
        if (data && hasFiniteRange(data)) {
          const buffered = applyAxisBuffer(data.min, data.max)
          data.min = buffered.min
          data.max = buffered.max
        }
      })
    }

    if (!autoScaleMode) {
      const cache = sensorRangesCustomScaleCache.current
      const merged: SensorRangeMap = {}
      Object.keys(sensorData).forEach((itemId) => {
        const frozen = cache?.[itemId]
        merged[itemId] =
          frozen && hasFiniteRange(frozen) ? frozen : sensorData[itemId]
      })

      // Keep the map's identity stable while fully frozen so downstream memos
      // don't churn on every recompute.
      if (cache) {
        const cacheKeys = Object.keys(cache)
        const mergedKeys = Object.keys(merged)
        if (
          cacheKeys.length === mergedKeys.length &&
          mergedKeys.every((itemId) => merged[itemId] === cache[itemId])
        ) {
          return cache
        }
      }

      sensorRangesCustomScaleCache.current = merged
      return merged
    }

    sensorRangesCustomScaleCache.current = sensorData
    return sensorData
  }, [
    selectedSensors,
    derivedValues,
    singleScaleMode,
    unitMapping,
    autoScaleMode,
    chartData,
  ])

  /**
   * Prune axis state whose pen no longer exists. Removal can originate from
   * several surfaces (pen drawer, table row, a config re-apply), so it is
   * handled reactively here rather than in each removal path. Without this, a
   * removed pen's custom range keeps `hasAnyCustomBound` true and auto-scale
   * stays wedged off with nothing visible left to clear — and local state then
   * persists the wedge across refreshes. The shared `single-scale` entry is
   * only kept while single-scale mode is active.
   */
  useEffect(() => {
    const currentItemIds = new Set([
      ...selectedSensors.map((sensor) => sensor.id),
      ...derivedValues.map((derivedValue) => derivedValue.id),
    ])

    const prunedRanges = pruneCustomAxisRanges(
      customAxisRanges,
      currentItemIds,
      singleScaleMode
    )
    if (prunedRanges !== customAxisRanges) {
      setCustomAxisRanges(prunedRanges)
      // Only ever re-ENABLE auto here (the un-wedge): hydration legitimately
      // keeps custom ranges around while auto-scale is on, so pruning must not
      // force the flag off when bounds remain.
      if (!hasAnyCustomBound(prunedRanges)) {
        setAutoScaleMode(true)
      }
    }

    const staleAssignmentIds = Object.keys(sensorAxisAssignment).filter(
      (itemId) => !currentItemIds.has(itemId)
    )
    if (staleAssignmentIds.length > 0) {
      setSensorAxisAssignment((prev) => {
        const next = { ...prev }
        staleAssignmentIds.forEach((itemId) => delete next[itemId])
        return next
      })
    }
  }, [
    selectedSensors,
    derivedValues,
    singleScaleMode,
    customAxisRanges,
    sensorAxisAssignment,
  ])

  /**
   * Auto-scale is not a mode the user toggles anymore: it simply means "no
   * custom overrides". Typing a value overrides (auto turns off); clearing
   * everything — per pen or via the Auto button — turns it back on. The flag
   * itself stays as state because chart hydration/persistence set it directly.
   */
  const handleScaleModeToggle = useCallback(
    (newSingleScaleMode: boolean) => {
      if (newSingleScaleMode === singleScaleMode) {
        return
      }

      const allItems = getAllActiveItems(selectedSensors, derivedValues)
      const nextRanges = computeScaleModeTransitionRanges(
        newSingleScaleMode,
        allItems,
        sensorRanges,
        customAxisRanges,
        lastModifiedBoundRef.current
      )

      if (nextRanges !== customAxisRanges) {
        setCustomAxisRanges(nextRanges)
        setAutoScaleMode(!hasAnyCustomBound(nextRanges))
      }
      setSingleScaleMode(newSingleScaleMode)
    },
    [
      singleScaleMode,
      selectedSensors,
      derivedValues,
      sensorRanges,
      customAxisRanges,
    ]
  )

  const handleAxisRangeChange = useCallback(
    (sensorId: string, value: string, type: 'max' | 'min') => {
      const parsed = parseCommittedNumber(value)
      if (!parsed.ok) {
        return
      }
      const numValue = parsed.value

      const allItems = getAllActiveItems(selectedSensors, derivedValues)
      const linkedItemIds = singleScaleMode
        ? allItems.map((item) => item.id)
        : null

      // The auto bounds a target falls back to when its custom counterpart is
      // missing — the inversion guard checks against these so a bound that
      // crosses the RENDERED counterpart is rejected, not just one that
      // crosses another custom bound. In single mode every pen (and the
      // shared 'single-scale' entry) renders the one shared auto range.
      let autoRanges: Record<string, { min?: number; max?: number }>
      if (singleScaleMode) {
        autoRanges = {}
        const shared = getSharedAutoRange(allItems, sensorRanges)
        if (shared) {
          allItems.forEach((item) => {
            autoRanges[item.id] = shared
          })
          autoRanges['single-scale'] = shared
        }
      } else {
        autoRanges = sensorRanges
      }

      const nextRanges = applyAxisBoundEdit(
        customAxisRanges,
        linkedItemIds,
        sensorId,
        type,
        numValue,
        autoRanges
      )
      // Identity-equal means the edit was rejected (it would invert the range
      // into min > max, which the chart schema refuses to persist): revert to
      // the previous value — commit nothing, remember nothing.
      if (nextRanges === customAxisRanges) {
        return
      }

      if (numValue !== undefined) {
        lastModifiedBoundRef.current = {
          ...lastModifiedBoundRef.current,
          [type]: numValue,
        }
      }

      setCustomAxisRanges(nextRanges)
      setAutoScaleMode(!hasAnyCustomBound(nextRanges))
    },
    [
      singleScaleMode,
      selectedSensors,
      derivedValues,
      customAxisRanges,
      sensorRanges,
    ]
  )

  const handleYMinChange = useCallback(
    (sensorId: string, value: string) =>
      handleAxisRangeChange(sensorId, value, 'min'),
    [handleAxisRangeChange]
  )

  const handleYMaxChange = useCallback(
    (sensorId: string, value: string) =>
      handleAxisRangeChange(sensorId, value, 'max'),
    [handleAxisRangeChange]
  )

  /** Per-pen Auto: clears that pen's custom range so it auto-scales again. */
  const handleClearAxisRange = useCallback(
    (sensorId: string) => {
      if (!customAxisRanges[sensorId]) {
        return
      }
      const nextRanges = { ...customAxisRanges }
      delete nextRanges[sensorId]
      setCustomAxisRanges(nextRanges)
      setAutoScaleMode(!hasAnyCustomBound(nextRanges))
    },
    [customAxisRanges]
  )

  /** Auto for everything: clears all custom ranges so every pen auto-scales. */
  const handleAutoScaleAll = useCallback(() => {
    lastModifiedBoundRef.current = {}
    setCustomAxisRanges({})
    setAutoScaleMode(true)
  }, [])

  const sensorAxisMapping = useMemo(() => {
    const mapping: SensorAxisMapping = {}

    const allItems = getAllActiveItems(selectedSensors, derivedValues)

    // Assign axes to all items (sensors and derived values)
    if (singleScaleMode) {
      // In single scale mode, all items share the same axis
      allItems.forEach(({ id: itemId }) => {
        mapping[itemId] = {
          axis: 'y',
          side: 'left',
          index: 0,
        }
      })
    } else {
      // In multi scale mode, each item gets its own axis
      allItems.forEach(({ id: itemId }, index) => {
        // Check if user has manually assigned this item
        const userAssignment = sensorAxisAssignment[itemId]

        if (userAssignment) {
          mapping[itemId] = {
            axis: index === 0 ? 'y' : `y${index + 1}`,
            side: userAssignment.side,
            index: index,
          }
        } else {
          mapping[itemId] = {
            axis: index === 0 ? 'y' : `y${index + 1}`,
            side: 'left',
            index: index,
          }
        }
      })
    }

    return mapping
  }, [selectedSensors, derivedValues, singleScaleMode, sensorAxisAssignment])

  return {
    // Chart display settings
    singleScaleMode,
    setSingleScaleMode,
    autoScaleMode,
    setAutoScaleMode,
    expandedAxes,
    setExpandedAxes,
    xAxisSensorId,
    setXAxisSensorId,
    // Axis/scale management
    customAxisRanges,
    setCustomAxisRanges,
    customXAxisRange,
    setCustomXAxisRange,
    sensorAxisAssignment,
    setSensorAxisAssignment,
    sensorAxisMapping,
    handleScaleModeToggle,
    handleAxisRangeChange,
    handleYMinChange,
    handleYMaxChange,
    handleClearAxisRange,
    handleAutoScaleAll,
    sensorRanges,
    highlightedSensorId,
    setHighlightedSensorId,
    secondaryHighlightedSensorIds,
    setSecondaryHighlightedSensorIds,
    hideAllAlerts,
    setHideAllAlerts,
  }
}
